import React, { useContext, useState, useEffect, useRef } from 'react';
import { io } from 'socket.io-client';
import { OrderDataProviderContext } from '../../context-api/data-provider/OrderDataProvider';
import { API_NETWORK_ERROR, localStorageKeys } from '../../utils/constants';
import { GlobalStateDataContext } from '../../context-api/data-provider/GlobalStateDataProvider';
import ActionOrders from './ActionOrders';
import InProgressOrder from './InProgressOrder';
import ReadyOrder from './ReadyOrder';
import ScheduledOrder from './ScheduledOrder';
import NewOrderAlert from '../../composed/neworderalert/NewOrderAlert';
import AtomicDialog from '../../atomic/dialog/Dialog';
import AllOrders from './AllOrders';
import moment from 'moment-timezone';
import { Error404 } from '../../composed/error404/Error404';
import RestaurantDelivery from './RestaurantDelivery';
import './order-dashboard-layout.scss';
import ReferralStatistics from '../../composed/referralStatistics/ReferralStatistics';
import { SignUpDataContext } from '../../context-api/data-provider/SignupDataProvider';
import { useNavigate } from 'react-router-dom';

const formattedDateTime = () => {
  return moment().format('MM/DD/YYYY - hh:mm A');
};

const OrderMainDashboard = () => {
  const { TOKEN } = localStorageKeys;
  const navigate = useNavigate();
  const { getOrderAPIResponse, FetchOrders, checkOrderTime, KitchenName, currentDate } =
    useContext(OrderDataProviderContext);

  const { onLoadInfoResponse } = useContext(GlobalStateDataContext);
  const [orderDataFromSocket, setOrderDataFromSocket] = useState(null);

  const { selectedMenuTab, setOpenOrderAlert, newPendingOrder, allOrders, openOrderAlert } =
    useContext(GlobalStateDataContext);

  const { success, message, data, loading } = getOrderAPIResponse;

  // console.log('----newPendingOrder', newPendingOrder)
  // console.log('---- preparingOrder', preparingOrder)
  // console.log('-----setScheduledOrder', scheduledOrder)
  // console.log('---- orderReadyForPickup', orderReadyForPickup)
  // console.log('---allOrders', allOrders)
  // console.log('---getOrderAPIResponse', getOrderAPIResponse)

  useEffect(() => {
    if (orderDataFromSocket) {
      checkOrderTime(orderDataFromSocket);
    }
  }, [JSON.stringify(orderDataFromSocket)]);

  const socketRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const dbFetchedOrderNoRef = useRef(new Set());
  const [connectionStatus, setConnectionStatus] = useState('Disconnected');
  const [notificationServerDown, setNotificationServerDown] = useState(false);
  const URL_PATH_NOTIFICATION_SERVER = process.env.REACT_APP_NOTIFICATION_SERVER_URL; // WE HAVE TO REPLACE BEFORE GOING GIVE

  const { onLogout } = useContext(SignUpDataContext);
  const RedirectToLogin = async () => navigate('/login');

  const triggerLogout = async () => {
    await onLogout();
    await RedirectToLogin();
    window.location.reload();
  };

  // ============================================================
  // KITCHEN NOTIFICATIONS
  // API = Source of truth for orders
  // Socket.IO = Notification + automatic acknowledgment
  // Redis = Recovery of missed notifications
  // ============================================================

  const alertedOrderNosRef = useRef(new Set());
  const initialOrdersLoadedRef = useRef(false);

  const fetchOrdersRef = useRef(FetchOrders);
  fetchOrdersRef.current = FetchOrders;

  const currentDateRef = useRef(currentDate);
  currentDateRef.current = currentDate;

  const checkOrderTimeRef = useRef(checkOrderTime);
  checkOrderTimeRef.current = checkOrderTime;

  const refreshPromiseRef = useRef(null);
  const refreshAgainRef = useRef(false);

  const isPendingToday = (order) => {
    if (!order?.order_no || order?.internal_order_status !== 'pending') {
      return false;
    }

    return order.delivery_type === 'pickup'
      ? order.order_date === currentDateRef.current
      : order.delivery_date === currentDateRef.current;
  };

  // ============================================================
  // FETCH ORDERS AND DETECT NEW ORDERS
  // ============================================================

  const refreshKitchenOrders = async () => {
    if (refreshPromiseRef.current) {
      refreshAgainRef.current = true;
      return refreshPromiseRef.current;
    }

    const refreshTask = async () => {
      let latestResult;

      do {
        refreshAgainRef.current = false;

        const requestedDate = currentDateRef.current;

        const orderData = await fetchOrdersRef.current({
          date: requestedDate,
        });

        latestResult = orderData;

        // Do not use an outdated response for alert tracking.
        if (requestedDate !== currentDateRef.current) {
          refreshAgainRef.current = true;
          continue;
        }

        const orders = Array.isArray(orderData?.payAsYouGoOrders) ? orderData.payAsYouGoOrders : [];

        const storeType = onLoadInfoResponse?.data?.data?.data?.KitchenInfo?.account_type;

        // Initial login/page refresh:
        // Register existing pending orders without ringing.
        if (!initialOrdersLoadedRef.current) {
          orders.forEach((order) => {
            if (isPendingToday(order)) {
              alertedOrderNosRef.current.add(order.order_no);
            }
          });

          initialOrdersLoadedRef.current = true;

          console.log('Initial kitchen orders loaded:', orders.length);
        } else if (storeType === 'restaurant') {
          const newOrders = orders.filter(
            (order) => isPendingToday(order) && !alertedOrderNosRef.current.has(order.order_no)
          );

          if (newOrders.length > 0) {
            newOrders.forEach((order) => {
              alertedOrderNosRef.current.add(order.order_no);

              console.log('New kitchen order discovered through API:', order.order_no);

              // Preserve existing order-time handling.
              checkOrderTimeRef.current(order);
            });

            setOpenOrderAlert(true);
          }
        }

        console.log('Kitchen orders refreshed from API');
      } while (refreshAgainRef.current);

      return latestResult;
    };

    const promise = refreshTask();
    refreshPromiseRef.current = promise;

    try {
      return await promise;
    } finally {
      if (refreshPromiseRef.current === promise) {
        refreshPromiseRef.current = null;
      }
    }
  };

  // ============================================================
  // SOCKET.IO CONNECTION AND NOTIFICATIONS
  // ============================================================

  useEffect(() => {
    const socket = io(URL_PATH_NOTIFICATION_SERVER, {
      autoConnect: false,
      reconnection: true,
      reconnectionAttempts: Infinity,
      forceNew: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 2000,
      timeout: 5000,
      query: {
        type: 'kitchen_frontend_app',
      },
      transports: ['websocket'],
      auth: {
        token: localStorage.getItem(TOKEN),
      },
    });

    socketRef.current = socket;

    // ------------------------------------------------------------
    // RECEIVE SOCKET.IO NOTIFICATION
    // ------------------------------------------------------------

    const handleKitchenNotification = (data, callback) => {
      try {
        // Ignore heartbeat responses.
        if (!data?.order_no) {
          return;
        }

        // Validate kitchen.
        if (data?.kitchen_name !== KitchenName) {
          console.error('Kitchen notification mismatch:', {
            expectedKitchen: KitchenName,
            receivedKitchen: data?.kitchen_name,
            orderNo: data?.order_no,
          });

          if (typeof callback === 'function') {
            callback({
              received: false,
              kitchenName: KitchenName,
              order_no: data?.order_no,
              message: 'Notification belongs to another kitchen',
            });
          }

          return;
        }

        console.log('Kitchen notification received:', data.order_no);

        // --------------------------------------------------------
        // AUTOMATIC ACKNOWLEDGMENT
        //
        // Acknowledges that the frontend received the signal.
        // Notification Server saves ACK to MongoDB.
        // Redis key is deleted only after server-side DB confirmation.
        // --------------------------------------------------------

        if (typeof callback === 'function') {
          callback({
            received: true,
            kitchenName: KitchenName,
            order_no: data.order_no,
            joined_order_no: data?.joined_order_no ?? null,
            receivedAt: new Date().toISOString(),
            message: 'Kitchen frontend received notification',
          });
        }

        // --------------------------------------------------------
        // FETCH ORDERS THROUGH API
        //
        // Do not add Socket.IO payload to dashboard state.
        // --------------------------------------------------------

        refreshKitchenOrders().catch((error) => {
          console.error('Order API refresh after notification failed:', error);
        });
      } catch (error) {
        console.error('Kitchen notification handling error:', error);

        if (typeof callback === 'function') {
          callback({
            received: false,
            kitchenName: KitchenName,
            order_no: data?.order_no ?? null,
            message: error.message,
          });
        }
      }
    };

    // Register listeners before connecting.
    socket.on(KitchenName, handleKitchenNotification);
    socket.on(`pong-${KitchenName}`, handleKitchenNotification);

    // ------------------------------------------------------------
    // CONNECTED
    // ------------------------------------------------------------

    socket.on('connect', () => {
      reconnectAttemptsRef.current = 0;

      setConnectionStatus('Connected');
      setNotificationServerDown(false);

      console.log(`Kitchen connected: ${socket.id} ${formattedDateTime()}`);

      // Fetch latest orders after connection.
      refreshKitchenOrders().catch((error) => {
        console.error('Connect API refresh failed:', error);
      });

      // Recover missed Redis notifications on initial connection
      // and every reconnection.
      socket.emit('recover-kitchen-notifications');
    });

    // ------------------------------------------------------------
    // DISCONNECTED
    // ------------------------------------------------------------

    socket.on('disconnect', (reason) => {
      setConnectionStatus(`Disconnected: ${reason}`);
      setNotificationServerDown(true);

      console.log(`Kitchen disconnected: ${reason} ${formattedDateTime()}`);

      // No page reload.
      // API polling handles orders while disconnected.
    });

    // ------------------------------------------------------------
    // RECONNECTION
    // ------------------------------------------------------------

    const handleReconnectAttempt = (attemptNumber) => {
      reconnectAttemptsRef.current = attemptNumber;

      setConnectionStatus(`Reconnecting... Attempt ${attemptNumber}`);
    };

    const handleReconnectFailed = () => {
      setConnectionStatus('Reconnection failed');
      setNotificationServerDown(true);

      console.error('Notification server reconnect failed');
    };

    const handleReconnect = () => {
      console.log('Kitchen Notification Server reconnected');

      // The connect handler already refreshes orders
      // and requests Redis recovery.
    };

    socket.io.on('reconnect_attempt', handleReconnectAttempt);
    socket.io.on('reconnect_failed', handleReconnectFailed);
    socket.io.on('reconnect', handleReconnect);

    socket.on('connect_error', (error) => {
      setNotificationServerDown(true);

      console.error('Notification Server connection error:', error.message);
    });

    socket.on('socket-auth-error', (data) => {
      console.error('Socket authentication error:', data);

      if (data?.message === 'Invalid token') {
        setNotificationServerDown(true);
        triggerLogout();
      }
    });

    // ------------------------------------------------------------
    // HEARTBEAT
    // ------------------------------------------------------------

    const pingInterval = setInterval(() => {
      if (socket.connected) {
        socket.emit('ping-from-kitchen');

        console.log(`Kitchen heartbeat sent: ${formattedDateTime()}`);
      }
    }, 60000);

    // ------------------------------------------------------------
    // REDIS RECOVERY - Every 2 minutes
    // ------------------------------------------------------------

    const recoveryInterval = setInterval(() => {
      if (socket.connected) {
        socket.emit('recover-kitchen-notifications');

        console.log(`Kitchen Redis recovery requested: ${formattedDateTime()}`);
      }
    }, 120000);

    socket.connect();

    return () => {
      clearInterval(pingInterval);
      clearInterval(recoveryInterval);

      socket.off(KitchenName, handleKitchenNotification);
      socket.off(`pong-${KitchenName}`, handleKitchenNotification);
      socket.io.off('reconnect_attempt', handleReconnectAttempt);
      socket.io.off('reconnect_failed', handleReconnectFailed);
      socket.io.off('reconnect', handleReconnect);
      socket.disconnect();

      if (socketRef.current === socket) {
        socketRef.current = null;
      }
    };
  }, [KitchenName, currentDate]);

  // ============================================================
  // INITIAL API FETCH + OFFLINE POLLING
  // ============================================================

  useEffect(() => {
    let interval = null;
    let cancelled = false;

    // Reset initial order tracking when the kitchen/date changes.
    initialOrdersLoadedRef.current = false;
    alertedOrderNosRef.current.clear();

    const fetchData = async () => {
      if (cancelled) return;

      try {
        await refreshKitchenOrders();
      } catch (error) {
        console.error('Kitchen API fetching error:', error);
      }
    };

    // Fetch immediately on login or date change.
    fetchData();

    // Poll every 5 minutes while disconnected.
    if (notificationServerDown) {
      interval = setInterval(fetchData, 300000);
    }

    return () => {
      cancelled = true;

      if (interval) {
        clearInterval(interval);
      }
    };
  }, [notificationServerDown, currentDate]);

  if (success === false) {
    if (message === 'Request timed out') {
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            fontFamily: 'Inter Medium',
            fontSize: '20px',
            color: 'red',
            marginTop: '20px',
          }}
        >
          {' '}
          The request took too long. Please try again.
        </div>
      );
    } else if (message === API_NETWORK_ERROR) {
      return <Error404 />;
    } else if (message === 'Authentication failed!') {
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            fontFamily: 'Inter Medium',
            fontSize: '20px',
            color: 'red',
            marginTop: '20px',
          }}
        >
          {' '}
          Session expired — please log in again to continue.
        </div>
      );
    } else {
      console.log(
        '----We are working on fixing an unexpected issue. Please check back shortly',
        message
      );
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            fontFamily: 'Inter Medium',
            fontSize: '20px',
            color: 'red',
            marginTop: '20px',
          }}
        >
          {' '}
          We're working on fixing an unexpected issue. Please check back shortly.
        </div>
      );
    }
  }

  return (
    <div
      className={
        selectedMenuTab === 'restaurantdelivery'
          ? 'order-dashboard order-dashboard--map-tab'
          : 'order-dashboard'
      }
    >
      {newPendingOrder?.length <= 0 && selectedMenuTab === 'action' && (
        <ReferralStatistics redirect={true} />
      )}
      {selectedMenuTab === 'action' && <ActionOrders />}
      {selectedMenuTab === 'inprogress' && <InProgressOrder />}
      {selectedMenuTab === 'orderReady' && <ReadyOrder />}
      {selectedMenuTab === 'allorders' && <AllOrders />}
      {selectedMenuTab === 'restaurantdelivery' && <RestaurantDelivery />}
      <ScheduledOrder />

      <AtomicDialog>
        <NewOrderAlert />
      </AtomicDialog>
    </div>
  );
};

export default OrderMainDashboard;
